import { translate } from 'locales'
import { formatLocalCap } from 'utils/date'
import React from 'react'
import {
  IFinancialMonthDetail,
  IFinancialMonthlyProduction,
  MonthlyProductionStatus,
} from '../types'
import { formatCurrency, formatDate } from '../utils'
import * as S from './styles'
import {
  STATUS_GRAY,
  STATUS_GREEN,
  statusColor,
  statusLabelKey,
} from './statusColors'

// Previsão de pagamento oculta até as novas regras de negócio
const SHOW_PAYMENT_FORECAST = false

interface ProductionTimelineProps {
  months: IFinancialMonthlyProduction[]
  selectedMonth: string
  selectedDetail: IFinancialMonthDetail
  locale: string
  onSelect: (periodMonth: string) => void
}

const badgeColorsByStatus = (status: MonthlyProductionStatus) => {
  if (status === 'paid') return { background: '#e3f6ef', color: STATUS_GREEN }
  if (status === 'current')
    return { background: '#fef8e9', color: STATUS_GRAY }
  return { background: '#efefef', color: STATUS_GRAY }
}

const ProductionTimeline = ({
  months,
  selectedMonth,
  selectedDetail,
  locale,
  onSelect,
}: ProductionTimelineProps) => {
  const selectedProduction = months.find(
    (month) => month.periodMonth === selectedMonth
  )
  const footerStatus = selectedProduction?.status ?? 'future'
  const footerStatusColor = statusColor(footerStatus)

  return (
    <S.ProductionSection>
      <h2>{translate('financial.overview.monthlyProductionTitle')}</h2>
      <span className="section-subtitle">
        {translate('financial.overview.monthlyProductionSubtitle')}
      </span>

      <S.Timeline>
        {months.map((month) => {
          const monthLabel = formatLocalCap(`${month.periodMonth}-01`, 'MMM')
          const paymentLabel = month.paymentMonth
            ? formatLocalCap(`${month.paymentMonth}-01`, 'MMM')
            : null

          return (
            <S.MonthButton
              key={month.periodMonth}
              type="button"
              isSelected={month.periodMonth === selectedMonth}
              disabled={month.status === 'future'}
              onClick={() => onSelect(month.periodMonth)}
            >
              <span className="month-label">{monthLabel}</span>

              <S.Dot color={statusColor(month.status)} size={10} />

              <span className="month-value">
                {formatCurrency(month.totalValue, locale)}
              </span>

              {SHOW_PAYMENT_FORECAST && month.status === 'paid' && paymentLabel && (
                <span
                  className="month-badge"
                  style={badgeColorsByStatus(month.status)}
                >
                  {translate('financial.overview.paidOn')} {paymentLabel}
                </span>
              )}

              {SHOW_PAYMENT_FORECAST &&
                (month.status === 'closed' || month.status === 'current') &&
                paymentLabel && (
                  <span
                    className="month-badge"
                    style={badgeColorsByStatus(month.status)}
                  >
                    {translate('financial.overview.willBePaidOn')} {paymentLabel}
                  </span>
                )}
            </S.MonthButton>
          )
        })}
      </S.Timeline>

      <S.ProductionFooter>
        <div className="closing-status">
          <div className="status-row" style={{ color: footerStatusColor }}>
            <S.Dot color={footerStatusColor} />
            {translate(statusLabelKey(footerStatus))}
          </div>

          <div className="dates">
            <div>
              <span className="label">{translate('financial.closingDate')}</span>
              <br />
              <span className="value">{formatDate(selectedDetail.closingDate)}</span>
            </div>

            {SHOW_PAYMENT_FORECAST && (
              <div>
                <span className="label">
                  {translate('financial.overview.expected')}
                </span>
                <br />
                <span className="value">
                  {selectedDetail.paymentMonth
                    ? formatLocalCap(
                        `${selectedDetail.paymentMonth}-01`,
                        'MMM yyyy'
                      )
                    : translate('financial.notAvailable')}
                </span>
              </div>
            )}
          </div>
        </div>

        <div className="accumulated">
          <span className="label">
            {translate('financial.overview.accumulated')}
          </span>
          <br />
          <span className="value">
            {formatCurrency(selectedDetail.totalValue, locale)}
          </span>
        </div>
      </S.ProductionFooter>
    </S.ProductionSection>
  )
}

export default ProductionTimeline
